import { LoopOutlined, SwapHoriz } from '@mui/icons-material';
import {
  Box,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';
import {
  useGetGiftContributorPayments,
  useGetGiftContributorRecipients
} from '@portal-middleware/hooks';
import { useGetSessionInfo } from '@sentinel/hooks';
import { formatCurrency, formatDate } from '@vestwell-frontend/helpers';
import { GiftIcon, Text } from '@vestwell-frontend/ui';

import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';

const StyledIconTableCell = styled(TableCell)(({ theme }) => ({
  paddingRight: theme.spacing(0),
  width: theme.spacing(1)
}));

const StyledBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.water.light,
  borderRadius: theme.spacing(8),
  display: 'inline-block',
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(12),
  padding: theme.spacing(2)
}));

const GiftPaymentsList: FC = () => {
  const { giftingId } = useParams();
  const user = useGetSessionInfo('user', {
    query: {
      suspense: true
    }
  });

  const payments = useGetGiftContributorPayments(user?.data?.giftContributorId);

  const recipients = useGetGiftContributorRecipients(
    user?.data?.giftContributorId,
    {
      query: {
        suspense: true
      }
    }
  );

  const validGiftsPayments = useMemo(() => {
    const activeRecipient = recipients?.data?.data?.find(
      recipient => recipient.encodedAccountId === giftingId
    );
    return payments?.data?.data?.filter(gift => {
      return gift.giftAccountId === activeRecipient?.giftAccountId;
    });
  }, [payments, recipients, giftingId]);

  return (
    <>
      <StyledBox>
        <SwapHoriz color='primary' />
      </StyledBox>
      <Stack>
        <Text aria-level={2} variant='f2'>
          Recent Gifts
        </Text>
        <Stack gap={4}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell colSpan={2}>Activity</TableCell>
                <TableCell align='right'>Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {validGiftsPayments?.map(gift => {
                return (
                  <TableRow key={gift.id}>
                    <StyledIconTableCell>
                      {gift.recurringContributionsId ? (
                        <LoopOutlined />
                      ) : (
                        <GiftIcon />
                      )}{' '}
                    </StyledIconTableCell>
                    <TableCell>
                      <Text fontWeight={800} mb={0}>
                        {gift.occasion.name} Gift
                      </Text>
                      <Text mb={0} variant='i1'>
                        {formatDate(gift.postedDate)}
                      </Text>
                    </TableCell>
                    <TableCell align='right'>
                      <Text mb={0}>{formatCurrency(gift.grossAmount)}</Text>
                    </TableCell>
                  </TableRow>
                );
              })}
              {validGiftsPayments?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Text variant='i1'>No recent gifts</Text>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Stack>
      </Stack>
    </>
  );
};

export default GiftPaymentsList;
